import { Link } from 'react-router-dom'
import { StartClubCta } from '@/components/marketing/StartClubCta'
import styles from './HomeSections.module.css'

const WORKSPACE_ROUTE = '/workspace'

/** § Problem — the unnamed competitive-framing hook (T3 §1.2). */
export function HomeProblem() {
  return (
    <section
      className={styles.narrative}
      aria-labelledby="home-problem-title"
      data-track-section
      data-enter="right"
    >
      <h2 id="home-problem-title" className={styles.narrativeTitle}>
        Booking tools run your club. The Racket Center grows it.
      </h2>
      <p className={styles.narrativeBody}>
        A booking calendar keeps your courts organized — but an empty slot is still empty. The Racket
        Center is built around the thing that actually fills courts and keeps members coming back:
        real, rated, competitive play. Tournaments to enter, a rating that moves after every match,
        ladders and leagues that give each week a reason to show up.
      </p>
    </section>
  )
}

/** § Network — the marketplace as a B2B argument (T3 §1.2). */
export function HomeNetwork() {
  return (
    <section
      className={styles.narrative}
      aria-labelledby="home-network-title"
      data-track-section
      data-enter="right"
    >
      <h2 id="home-network-title" className={styles.narrativeTitle}>
        Your club joins a network players already use
      </h2>
      <p className={styles.narrativeBody}>
        The Racket Center is more than software — it&apos;s where players in your area already come to
        find clubs, tournaments and matches. List your club once and you&apos;re part of a marketplace
        players browse, not just a private admin tool. Software plus an audience.
      </p>
      <Link className={styles.quietLink} to="/play">
        See where players play <span aria-hidden="true">→</span>
      </Link>
    </section>
  )
}

const STEPS = [
  {
    label: 'Create your club — free',
    line: 'Set up in minutes. No card, no sales call.',
  },
  {
    label: 'Add your courts, players and first event',
    line: 'Invite members, lay out your schedule, open registration.',
  },
  {
    label: 'Run play, rate players, fill courts',
    line: 'Run tournaments and ladders, let ratings update themselves, and market to fill the gaps.',
  },
]

/** § How it works — 3 owner-framed steps (T3 §1.2). */
export function HomeHowItWorks() {
  return (
    <section className={styles.how} aria-labelledby="home-how-title" data-track-section>
      <h2 id="home-how-title" className={styles.narrativeTitle} data-enter="right">
        Up and running in three steps
      </h2>
      <ol className={styles.steps} data-unfold>
        {STEPS.map((step, index) => (
          <li key={step.label} className={styles.step} data-track-light>
            <span className={styles.stepNum} aria-hidden="true">
              {String(index + 1).padStart(2, '0')}
            </span>
            <span className={styles.stepText}>
              <span className={styles.stepLabel}>{step.label}</span>
              <span className={styles.stepLine}>{step.line}</span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

/** § Final CTA — the closing conversion block (T3 §1.2). */
export function HomeFinalCta() {
  return (
    <section
      className={styles.finalCta}
      aria-labelledby="home-final-title"
      data-track-landing
      data-enter="assemble"
    >
      <h2 id="home-final-title" className={styles.finalTitle}>
        Make your club the place to compete
      </h2>
      <p className={styles.finalSub}>Start free today — every feature included, no card required.</p>
      <StartClubCta
        source="home_closing"
        size="lg"
        to={WORKSPACE_ROUTE}
        className={styles.finalCtaBtn}
        data-track-land
      >
        Start your club — free
      </StartClubCta>
    </section>
  )
}
